import React from 'react';
import cs from 'classnames';
import { mergeRefs } from '../merge-refs/merge-refs';
/**
 * `AddProps` lets you add more props to a React element that was passed
 * in as a prop: `foo={<Bar />}`.
 *
 * `AddProps` is useful in the construction of reusable components, whether
 * those components live in your project or in a library like Maché.
 */
export const AddProps = React.forwardRef(function AddProps(props, ref) {
    const { to, className, style, ...rest } = props;
    // Remove all rest props that are passed as undefined
    // Use null if your intent is to override a previously passed value.
    Object.keys(rest).forEach((extraPropName) => {
        const extraPropValue = rest[extraPropName];
        if (extraPropValue === undefined) {
            delete rest[extraPropName];
        }
    });
    if (!React.isValidElement(to))
        return to;
    const { children, className: toClassName, style: toStyle, ...toRest } = to.props;
    const hasStyle = !!(style || toStyle);
    const childProps = {
        ...toRest,
        ...rest,
        className: cs(className, toClassName),
        style: hasStyle
            ? {
                ...(toStyle || {}),
                ...(style || {}),
            }
            : undefined,
    };
    // React.cloneElement() preserves the ref by default, so we
    // should only override that ref if one has actually been
    // passed
    if (ref) {
        if (to.ref) {
            childProps.ref = mergeRefs(to.ref, ref);
        }
        else {
            childProps.ref = ref;
        }
    }
    return React.cloneElement(to, childProps, children);
});
